import React, { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, formatValue } from "@/lib/api";
import { useFormat } from "@/contexts/FormatContext";
import { Loader2, ArrowDown, Sparkles } from "lucide-react";
import PlayerTag from "@/components/PlayerTag";
import DmeTag from "@/components/DmeTag";

const GOALS = [
  { id: "upgrade_rb", label: "Upgrade at RB", blurb: "Trades that land you a better starting RB, sending fair-value depth from where you're deep." },
  { id: "upgrade_wr", label: "Upgrade at WR", blurb: "Trades that land you a better starting WR, sending fair-value depth from where you're deep." },
  { id: "upgrade_qb", label: "Upgrade at QB", blurb: "Trades that land you a better starting QB, sending fair-value depth from where you're deep." },
  { id: "upgrade_te", label: "Upgrade at TE", blurb: "Trades that land you a better starting TE, sending fair-value depth from where you're deep." },
  { id: "elite_for_depth", label: "Elite → 2+ Depth", blurb: "Sell high: flip one elite player for two or three rotation pieces of matching total value." },
  { id: "depth_for_elite", label: "Depth → Elite", blurb: "Consolidate: package two or three depth pieces to land one elite player." },
];

function Side({ label, players, total, testid }) {
  return (
    <div className="border border-[var(--border)] rounded-md p-3" data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] tracking-widest text-[var(--text-3)] font-mono">{label}</div>
        {players.length > 1 && <div className="tabular text-xs text-[var(--text-3)]">Σ {formatValue(total)}</div>}
      </div>
      <div className="space-y-2">
        {players.map((p, j) => (
          <div key={`${p.player_id}-${j}`} className="flex justify-between items-center gap-2">
            <PlayerTag player={p} size={28} />
            <div className="tabular text-[var(--accent)] shrink-0">{formatValue(p.value)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function TradeFinder() {
  const [params] = useSearchParams();
  const [leagues, setLeagues] = useState([]);
  const [leagueId, setLeagueId] = useState(params.get("league") || "");
  const [goal, setGoal] = useState(params.get("goal") || "upgrade_rb");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const { format } = useFormat();

  useEffect(() => {
    api.get("/leagues").then(r => {
      setLeagues(r.data.leagues || []);
      if (!leagueId && r.data.leagues?.[0]) setLeagueId(r.data.leagues[0].league_id);
    });
  }, []); // eslint-disable-line

  useEffect(() => {
    if (!leagueId) return;
    setLoading(true);
    api.get(`/leagues/${leagueId}/trade-finder`, { params: { goal } })
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [leagueId, format, goal]);

  const activeGoal = GOALS.find(g => g.id === goal);

  return (
    <div className="space-y-6 fade-up">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="text-[var(--accent)] text-xs tracking-widest mb-1 font-mono">// FINDER</div>
          <h1 className="font-display text-4xl font-bold" data-testid="tf-heading">In-League Trade Finder</h1>
          <p className="text-[var(--text-2)] mt-2 max-w-xl">Pick your goal — the finder hunts every roster for fair, value-balanced trades that match what <span className="text-white">you</span> actually want to do.</p>
        </div>
        <select className="input-dark md:w-80" value={leagueId} onChange={e => setLeagueId(e.target.value)} data-testid="tf-league-select">
          <option value="">— Pick a league —</option>
          {leagues.map(l => <option key={l.league_id} value={l.league_id}>{l.name}</option>)}
        </select>
      </header>

      {/* Goal tabs */}
      <div className="flex gap-2 flex-wrap border-b border-[var(--border)] pb-3">
        {GOALS.map(g => (
          <button key={g.id} onClick={() => setGoal(g.id)} data-testid={`tf-tab-${g.id}`}
            className={`text-sm px-3 py-1.5 rounded-sm transition-colors ${goal === g.id ? "bg-[var(--accent)] text-black font-semibold" : "text-[var(--text-2)] hover:text-white border border-[var(--border)]"}`}>
            {g.label}
          </button>
        ))}
      </div>
      {activeGoal && <p className="text-sm text-[var(--text-3)] -mt-2" data-testid="tf-goal-blurb">{activeGoal.blurb}</p>}

      {loading && <div className="card-bordered p-12 flex items-center justify-center"><Loader2 className="w-6 h-6 spin text-[var(--accent)]" /></div>}

      {!loading && data && !data.owned && (
        <div className="card-bordered p-12 text-center text-[var(--text-2)]" data-testid="tf-owned-note">
          {data.note || "Link the Sleeper account that owns a team in this league to get personalized trade targets."}
        </div>
      )}

      {!loading && data && data.owned && (
        <div>
          <div className="text-xs tracking-widest text-[var(--text-3)] mb-3 font-mono" data-testid="tf-count">
            {data.suggestions.length} MATCH{data.suggestions.length === 1 ? "" : "ES"} FOR "{activeGoal?.label?.toUpperCase()}"
          </div>
          {data.suggestions.length === 0 ? (
            <div className="card-bordered p-12 text-center text-[var(--text-2)]" data-testid="tf-empty">
              No fair trade matches this goal in your league right now. Try another goal or format.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {data.suggestions.map((s, i) => (
                <div key={i} className="card-bordered p-5" data-testid={`tf-card-${i}`}>
                  <div className="flex items-center justify-between mb-4">
                    <Sparkles className="w-4 h-4 text-[var(--accent)]" />
                    <div className="text-xs tracking-widest text-[var(--text-3)] inline-flex items-center gap-1.5 font-mono">FAIRNESS <DmeTag /></div>
                    <div className={`font-mono font-semibold ${s.fairness >= 95 ? "text-emerald-400" : "text-amber-400"}`} data-testid={`tf-fair-${i}`}>{s.fairness}%</div>
                  </div>
                  <Side label="YOU SEND" players={s.you_send} total={s.send_total} testid={`tf-send-${i}`} />
                  <div className="flex justify-center py-2"><ArrowDown className="w-4 h-4 text-[var(--text-3)]" /></div>
                  <Side label={`YOU GET · from ${s.partner}`} players={s.you_get} total={s.get_total} testid={`tf-get-${i}`} />
                  {s.rationale && <p className="text-[11px] text-[var(--text-3)] mt-3 leading-relaxed" data-testid={`tf-why-${i}`}>{s.rationale}</p>}
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
